import React, { useState, useEffect } from 'react';

export default function DateCalculator() {
  const [calculationType, setCalculationType] = useState('difference');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // 오늘 날짜를 YYYY-MM-DD 형식으로 반환
  const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 컴포넌트 마운트 시 오늘 날짜로 초기화
  useEffect(() => {
    const today = getTodayFormatted();
    setStartDate(today);
    setEndDate(today);
  }, []);

  // 날짜 차이 계산
  const calculateDateDifference = () => {
    if (!startDate || !endDate) return;

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // 시간대 차이를 제거하기 위해 날짜만 설정
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const resultText = `${diffDays}일`;
    setResult(resultText);
    
    // 기록 저장
    const newHistoryItem = {
      type: 'difference',
      startDate,
      endDate,
      result: resultText,
      timestamp: new Date().toISOString()
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 9)]);
  };

  // 날짜 더하기/빼기 계산
  const calculateDateAddSubtract = () => {
    if (!startDate || !days || isNaN(days)) return;

    const start = new Date(startDate);
    const daysNum = parseInt(days);
    
    // 날짜 더하기/빼기
    const resultDate = new Date(start);
    resultDate.setDate(resultDate.getDate() + daysNum);
    
    // 결과 날짜 포맷팅
    const year = resultDate.getFullYear();
    const month = String(resultDate.getMonth() + 1).padStart(2, '0');
    const day = String(resultDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    const resultText = `${formattedDate} (${daysNum > 0 ? '+' : ''}${daysNum}일)`;
    setResult(resultText);
    
    // 기록 저장
    const newHistoryItem = {
      type: 'addSubtract',
      startDate,
      days: daysNum,
      result: resultText,
      timestamp: new Date().toISOString()
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 9)]);
  };

  // 계산 실행
  const calculate = () => {
    if (calculationType === 'difference') {
      calculateDateDifference();
    } else {
      calculateDateAddSubtract();
    }
  };

  // 날짜 형식 변환 (YYYY-MM-DD -> YYYY년 MM월 DD일)
  const formatDateKorean = (dateString) => {
    if (!dateString) return '';
    
    const [year, month, day] = dateString.split('-');
    return `${year}년 ${month}월 ${day}일`;
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">날짜 계산기</h1>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          계산 유형
        </label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="difference"
              checked={calculationType === 'difference'}
              onChange={() => setCalculationType('difference')}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">두 날짜 사이의 일수 계산</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="addSubtract"
              checked={calculationType === 'addSubtract'}
              onChange={() => setCalculationType('addSubtract')}
              className="form-radio h-4 w-4 text-indigo-600"
            />
            <span className="ml-2">날짜에 일수 더하기/빼기</span>
          </label>
        </div>
      </div>
      
      <div className="bg-white shadow overflow-hidden rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          {calculationType === 'difference' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  시작 날짜
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  종료 날짜
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  기준 날짜
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  더하거나 뺄 일수 (음수 입력 시 이전 날짜 계산)
                </label>
                <input
                  type="number"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="예: 30 또는 -7"
                />
              </div>
            </div>
          )}
          
          <button
            onClick={calculate}
            className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            계산하기
          </button>
        </div>
        
        {result && (
          <div className="bg-gray-50 px-4 py-5 sm:p-6 border-t border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">계산 결과</h3>
            <p className="text-xl font-semibold text-indigo-600">{result}</p>
          </div>
        )}
      </div>
      
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium mb-4">최근 계산 기록</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    입력값
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    결과
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.type === 'difference' ? '날짜 차이' : '날짜 계산'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.type === 'difference' 
                        ? `${formatDateKorean(item.startDate)} ~ ${formatDateKorean(item.endDate)}`
                        : `${formatDateKorean(item.startDate)} ${item.days > 0 ? '+' : ''}${item.days}일`
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
} 